import { DateTime } from "luxon";
import { TournamentHeader } from "./TournamentHeader";
import classNames from "classnames";
import { LiveMatch, Tournament } from "@portal-frontend-ssr/blast-data-api";
import { AdaptiveLink } from "@portal-frontend-ssr/ui";
import { GameId } from "@portal-frontend-ssr/blast-api";
import { generateTeamLogoSrc } from "~/shared/helpers/url/generateTeamLogoSrc";
import { generateTournamentLink } from "~/shared/helpers/tournaments/generateTournamentLink";
import { ClientOnly } from "remix-utils/client-only";
import { generateMatchPageUrl } from "~/shared/helpers/url/generateMatchPageUrl";
import { MatchPageLinkWrapper } from "~/components/MatchPageLinkWrapper";

const Score = ({ match, gameId }: { match: LiveMatch; gameId: string }) => {
  const isFinished = match.hasFinished;
  const isLive = match.hasStarted && !match.hasFinished;
  const isNotStarted = !match.hasStarted && !match.hasFinished;

  const getHour = (date: string) => {
    return DateTime.fromISO(date).toFormat("HH:mm");
  };

  const getLiveComponents = () => {
    if (gameId === "dota") {
      if (match.metadata?._t === "dota_match" && match.metadata?.externalStreamUrl) {
        return <span className="cursor-pointer uppercase">LIVE</span>;
      } else {
        return <span className="uppercase">Live</span>;
      }
    } else {
      return (
        <AdaptiveLink to={`/live`} className="text-white no-underline">
          <span className="cursor-pointer uppercase">Live</span>
        </AdaptiveLink>
      );
    }
  };

  return (
    <div
      className={classNames(
        "flex h-7 w-12 items-center justify-center gap-1 rounded-small bg-background-100 px-2 py-1 font-style-label-l4",
        {
          "bg-error": isLive,
        },
      )}
    >
      {isFinished && (
        <>
          <span>{match.teamAScore}</span>
          <span>-</span>
          <span>{match.teamBScore}</span>
        </>
      )}
      {isLive && getLiveComponents()}
      <ClientOnly>
        {() => isNotStarted && <time className="font-style-label-l4">{getHour(match.scheduledAt)}</time>}
      </ClientOnly>
    </div>
  );
};

export const LiveTournament = ({ gameId, tournament }: { gameId: GameId; tournament: Tournament }) => {
  const todaysMatches = tournament.todaysMatches;

  if (!todaysMatches) {
    return null;
  }

  const tournamentLink = generateTournamentLink({ gameId, tournamentId: tournament.id });

  return (
    <div className="flex select-none flex-col gap-3 rounded-small bg-background-95 p-3" key={tournament.id}>
      <AdaptiveLink to={tournamentLink} className="text-foreground-100 transition-all hover:text-foreground-95">
        <TournamentHeader tournament={tournament} />
      </AdaptiveLink>
      <span className="pt-1 font-style-label-l4">TODAYS MATCHES</span>
      <div className="flex flex-col gap-1">
        {todaysMatches.map((match) => {
          const matchPageUrl = generateMatchPageUrl({
            game: gameId,
            tournamentName: tournament.name,
            matchUuid: match.id,
            matchName: match.name,
            teamAName: match.teamA?.name,
            teamBName: match.teamB?.name,
          });
          return (
            <MatchPageLinkWrapper
              key={match.id}
              condition={(gameId === "cs" || gameId === "dota" || gameId === "rl") && matchPageUrl !== undefined}
              wrapper={(children) => (
                <AdaptiveLink
                  to={matchPageUrl ?? ""}
                  key={match.id}
                  className="flex justify-center gap-2 rounded-small bg-background-85 p-3 transition-all hover:bg-background-80"
                >
                  {children}
                </AdaptiveLink>
              )}
            >
              <>
                <div className="flex flex-1 items-center justify-end gap-2 overflow-hidden">
                  <span className="truncate text-right font-style-body-b3">{match.teamA?.name}</span>
                  <img
                    src={generateTeamLogoSrc({ id: match.teamA?.id ?? "", height: 20 })}
                    alt={match.teamA?.name}
                    className="size-5 object-contain"
                    loading="lazy"
                  />
                </div>
                <Score match={match} gameId={gameId} />
                <div className="flex flex-1 items-center justify-start gap-2 overflow-hidden">
                  <img
                    src={generateTeamLogoSrc({ id: match.teamB?.id ?? "", height: 20 })}
                    alt={match.teamB?.name}
                    className="size-5 object-contain"
                    loading="lazy"
                  />
                  <span className="truncate text-left font-style-body-b3">{match.teamB?.name}</span>
                </div>
              </>
            </MatchPageLinkWrapper>
          );
        })}
      </div>
    </div>
  );
};
